// The retention categories of Settings > Cleanup & Retention (documentation/architecture.md
// "Cleanup & retention"): one list drives both the read-only view and the edit form, so every
// category - including GPS location history - follows the same conventions.
import type { CleanupRetentionUpdate, CleanupStatus } from '../../services/admin';

export type RetentionKey =
  | 'recording_retention_days'
  | 'log_retention_days'
  | 'conversation_export_retention_days'
  | 'direct_message_retention_days'
  | 'location_history_retention_days'
  | 'temp_file_max_age_hours'
  | 'orphan_file_min_age_hours';

export interface RetentionCategory {
  key: RetentionKey;
  label: string;
  unit: 'days' | 'hours';
  description?: string;
  /** Upper bound the server enforces, if any. */
  max?: number;
  /** The cleanup step is skipped at 0 (set only through the environment; the form accepts >= 1). */
  zeroKeepsForever?: boolean;
}

export const RETENTION_CATEGORIES: RetentionCategory[] = [
  { key: 'recording_retention_days', label: 'Recordings', unit: 'days' },
  { key: 'log_retention_days', label: 'Operational log', unit: 'days', zeroKeepsForever: true },
  { key: 'conversation_export_retention_days', label: 'Conversation exports', unit: 'days' },
  { key: 'direct_message_retention_days', label: 'Direct messages', unit: 'days', zeroKeepsForever: true },
  {
    key: 'location_history_retention_days',
    label: 'Location history (GPS)',
    unit: 'days',
    max: 3650,
    zeroKeepsForever: true,
    description: "Historical GPS positions (location_pings). Each radio's latest position is always kept.",
  },
  { key: 'temp_file_max_age_hours', label: 'Temp files', unit: 'hours' },
  { key: 'orphan_file_min_age_hours', label: 'Orphan file safety age', unit: 'hours' },
];

export function formatRetention(category: RetentionCategory, value: number): string {
  if (value <= 0 && category.zeroKeepsForever) return 'Kept forever';
  return `${value} ${category.unit}`;
}

/** The form's starting values, taken from the effective (persisted) configuration. */
export function retentionForm(status: CleanupStatus): CleanupRetentionUpdate {
  const form: CleanupRetentionUpdate = {
    enabled: status.enabled,
    dry_run: status.dry_run,
    interval_hours: status.interval_hours,
    batch_size: status.retention.batch_size,
  };
  for (const c of RETENTION_CATEGORIES) form[c.key] = status.retention[c.key];
  return form;
}

/** Only the fields the administrator changed: an untouched value (for example 0 = kept forever,
 * set in the environment) is never re-sent, so it can neither be rejected nor overwritten. */
export function retentionChanges(initial: CleanupRetentionUpdate, form: CleanupRetentionUpdate): CleanupRetentionUpdate {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(form) as (keyof CleanupRetentionUpdate)[]) {
    if (form[key] !== initial[key]) out[key] = form[key];
  }
  return out as CleanupRetentionUpdate;
}

/** Mirrors the server's validation for the changed fields; null when valid. */
export function validateRetentionChanges(changes: CleanupRetentionUpdate): string | null {
  for (const c of RETENTION_CATEGORIES) {
    const v = changes[c.key];
    if (v === undefined) continue;
    if (!Number.isInteger(v) || v < 1) return `${c.label}: must be at least 1 ${c.unit}`;
    if (c.max !== undefined && v > c.max) return `${c.label}: must be at most ${c.max} ${c.unit}`;
  }
  for (const key of ['interval_hours', 'batch_size'] as const) {
    const v = changes[key];
    if (v !== undefined && (!Number.isInteger(v) || v < 1)) return `${key === 'interval_hours' ? 'Scheduled interval' : 'Batch size'}: must be at least 1`;
  }
  return null;
}
